import { SAVE_VERSION } from '../config/constants';
import { gameConfig, type GameSpeed } from '../config/gameConfig';
import { createCompetitors } from '../data/competitors';
import { GameClock } from '../simulation/GameClock';
import type { Competitor } from './Competitor';
import { createFinanceState, type FinanceState } from './Finance';
import type { GameEvent, Notification } from './GameEvent';
import type { GameTime } from './GameTime';
import type { ParkingLot } from './ParkingLot';
import { createPlayer, type Player } from './Player';

/**
 * The complete, serializable state of a game.
 *
 * INVARIANT: GameState must be pure data (JSON round-trippable). No class
 * instances, functions, or Phaser objects. This keeps the simulation portable
 * (web / mobile / multiplayer server / tests) — see GAME_SPEC section 1.
 *
 * Later phases extend this with parkingLots, competitors, city, events, and
 * historical metrics; those are intentionally omitted in Phase 1.
 */
export interface GameState {
  saveVersion: number;
  /** Wall-clock timestamp (real ms) of last save; 0 for a fresh game. */
  savedAt: number;

  time: GameTime;
  speed: GameSpeed;
  paused: boolean;

  /** RNG state so randomness is deterministic across save/load. */
  rngState: number;

  player: Player;

  /** All parking lots owned by the player (spec section 7). */
  parkingLots: ParkingLot[];

  /** Company finances: today's accrual + rolling daily P&L (spec 13/23/39). */
  finance: FinanceState;

  /** AI competitor companies (spec sections 20/21). */
  competitors: Competitor[];

  /** Currently active random events (spec sections 22/36). */
  events: GameEvent[];
  /** Recent notifications feed (capped), newest last (spec section 24). */
  notifications: Notification[];

  /** Campaign objective (spec section 38): met once value/share goal reached. */
  objectiveAchieved: boolean;
  objectiveAchievedAtMinute: number;
}

function createInitialTime(): GameTime {
  // Derive the full, self-consistent GameTime from the configured start date
  // via a single source of truth (totalMinutes). This guarantees dayOfWeek and
  // totalMinutes always match the calendar fields.
  const totalMinutes = GameClock.toTotalMinutes(gameConfig.startDate);
  return GameClock.fromTotalMinutes(totalMinutes);
}

export interface NewGameOptions {
  companyName?: string;
  playerId?: string;
  seed?: number;
}

export function createNewGameState(options: NewGameOptions = {}): GameState {
  const seed = options.seed ?? 0x1a2b3c4d;
  return {
    saveVersion: SAVE_VERSION,
    savedAt: 0,
    time: createInitialTime(),
    speed: gameConfig.defaultSpeed,
    paused: true, // start paused so the player can get oriented
    rngState: seed >>> 0,
    player: createPlayer(options.playerId ?? 'player-1', options.companyName),
    parkingLots: [],
    finance: createFinanceState(),
    competitors: createCompetitors(),
    events: [],
    notifications: [],
    objectiveAchieved: false,
    objectiveAchievedAtMinute: 0,
  };
}
